import {PageBase} from "../../components/pageBase/pageBase";
import {makeStyles} from "tss-react/mui";
import {AdminHeader} from "../ManagerView/AdminHeader";
import {Box, Stack, Switch, Button, Typography, LinearProgress, CircularProgress, ThemeProvider, TableContainer, Paper} from "@mui/material";
import {palette} from "../../constants";
import {useContext, useEffect, useMemo, useState} from "react";
import {INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {deepEqual} from "@buildwitt/shapeshift-common/v1/common";
import PersonIcon from "@mui/icons-material/Person";
import {ISettings} from "../../types/settings";
import {LocaleContext} from "../../contexts/localeContext";
import {adminTheme as createAdminTheme} from "../../templates/adminTheme";
import {AdminContainer} from "../ManagerView/AdminContainer";
import {useSSTranslation} from "../../i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

const useStyles = makeStyles()((theme) => ({
	adminSettingsPage: {
		maxWidth: theme.containers.constrained,
		marginLeft: "auto",
		marginRight: "auto",
		"& [class*='menuIcon']": {
			display: "none"
		}
	},
	pageBase: {
		paddingTop: "60px",
		"& [class*='content']": {
			maxWidth: "100%"
		}
	},
	settingInner: {
		maxWidth: "860px",
		paddingLeft: "20px",
		paddingRight: "20px",
		marginLeft: "auto",
		marginRight: "auto",
		marginTop: "40px"
	},
	adminSettingsWrapper: {
		borderRadius: "8px",
		maxWidth: "860px",
		marginLeft: "auto",
		marginRight: "auto",
		padding: "20px",
		border: "1px solid rgba(255,255,255,0.1)",
		background: palette.greyDark
	},
	adminSettingsSection: {
		marginBottom: theme.spacing(1)
	},
	titleContainer: {
		width: "100%",
		padding: "40px 0 0 0"
	},
	table: {
		width: "100%",
		borderTop: 0
	},
	settingsTile: {
		marginTop: "20px",
		marginBottom: "20px"
	},
	switchContainer: {
		alignItems: "center",
		display: "flex",
		gap: "10px",
		marginTop: "20px"
	},
	switch: {
		width: 28,
		height: 16,
		padding: 0,
		display: "flex",
		"&:active": {
			"& .MuiSwitch-thumb": {
				width: 15
			},
			"& .MuiSwitch-switchBase.Mui-checked": {
				transform: "translateX(9px)"
			}
		},
		"& .MuiSwitch-switchBase": {
			padding: 2,
			"&.Mui-checked": {
				transform: "translateX(12px)",
				color: "#fff",
				"& + .MuiSwitch-track": {
					opacity: 1
				}
			}
		},
		"& .MuiSwitch-thumb": {
			width: 12,
			height: 12,
			borderRadius: 6,
			transition: theme.transitions.create(["width"], {
				duration: 200
			})
		},
		"& .MuiSwitch-track": {
			borderRadius: 16 / 2,
			opacity: 1,
			boxSizing: "border-box"
		}
	},
	btnContainer: {
		paddingTop: "20px",
		gap: "14px",
		display: "flex",
		marginTop: "20px",
		flexWrap: "nowrap",
		justifyContent: "flex-start",
		"& button": {
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			minHeight: "46px",
			fontSize: "14px",
			fontWeight: 600,
			borderRadius: "150px",
			textTransform: "uppercase",
			padding: "17px 30px"
		},
		[theme.breakpoints.down("sm")]: {
			flexDirection: "row",
			gap: "8px",
			"& button": {
				minWidth: "120px"
			}
		}
	}
}));

export const SettingsPage: React.FC = () => {
	const {classes} = useStyles();
	const [me] = useApi<ISettings>("/api/user/me")
	const [updateUserResponse, updateUser] = useApi<ISettings>()
	const [formData, setFormData] = useState<ISettings>({notifyEmail: false, notifyInApp: false, preferredLanguage: "en", overDueSkilsReminder: false, dailySkillsReminder: false});
	const {notify} = useContext(NotificationContext)
	const changeMade = !deepEqual(formData, me.data);
	const [,setLocale] = useContext(LocaleContext)
	const {t} = useSSTranslation();

	const adminTheme = useMemo(() => createAdminTheme(), []);

	const redirectToAccountPage = (): void => {
		window.location.href = (process.env.REACT_APP_PROPELAUTH_URL + "/account")
	}

	useEffect(() => {
		if(me.data){
			setFormData(me.data)
		}
	}, [me])

	useEffect(() => {
		if(updateUserResponse.statusCode === 200){
			notify(t("notifications_settings_updated","Your details have been updated"), "Success", INotificationSeverity.success, 5000)
			setLocale({locale: updateUserResponse.data?.preferredLanguage})
		}
	}, [updateUserResponse])

	const handleUpdate = (): void => {
		if(updateUserResponse.isLoading || !changeMade)
			return;

		updateUser({
			url: "/api/user",
			data: formData,
			method: "PUT"
		})
	}

	return (
		<Box className={classes.adminSettingsPage}>
			<ThemeProvider theme={adminTheme}>
				<PageBase className={classes.pageBase} disableSideBar>
					<AdminContainer size="wide">
						<Box className={classes.settingInner}>
							<AdminHeader title={t("page_settings_title", "Training Preferences")} />
							{(!me.data || me.isLoading) ? <LinearProgress /> :
								<Box className={classes.adminSettingsWrapper}>
									<Box className={classes.adminSettingsSection}>
										<Box className={classes.settingsTile}>
											<Typography variant="h4">{t("page_settings_language", "Preferred language")}: </Typography>
											<Box className={classes.switchContainer}>
												<Typography>{t("page_settings_english", "English")}</Typography>
												<Switch
													className={classes.switch}
													checked={formData.preferredLanguage === "es-419"}
													onChange={e => setFormData(prev => ({...prev, preferredLanguage: e.target.checked ? "es-419" : "en"}))}
													data-test-id="settings-language-switch"
													aria-label={t("page_settings_language", "Preferred language")}
													inputProps={{"aria-describedby": "language-switch-description"}}
												/>
												<Typography>{t("page_settings_spanish", "Spanish")}</Typography>
												<Typography id="language-switch-description" sx={{display: "none"}}>
													Toggle between English and Spanish language preferences
												</Typography>
											</Box>
										</Box>
										<Box sx={{display: "none"}}>
											<Typography>Notifications</Typography>
											<Box sx={{display: "flex", gap: "10px"}}>
												<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
													<Switch className={classes.switch} checked={formData.notifyInApp} onChange={e => setFormData(prev => ({...prev, notifyInApp: e.target.checked}))}/>
													<Typography>In-app</Typography>
												</Stack>
											</Box>
										</Box>
										<Box sx={{display: "none"}}>
											<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
												<Switch className={classes.switch} checked={formData.notifyEmail} onChange={e => setFormData(prev => ({...prev, notifyEmail: e.target.checked}))}/>
												<Typography>Email</Typography>
											</Stack>
										</Box>
										<Box sx={{display: "none"}}>
											<Typography>Automatic reminders</Typography>
											<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
												<Switch className={classes.switch} checked={formData.dailySkillsReminder} onChange={e => setFormData(prev => ({...prev, dailySkillsReminder: e.target.checked}))}/>
												<Typography>Daily skills</Typography>
											</Stack>
										</Box>
										<Box style={{display: "none"}}>
											<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
												<Switch className={classes.switch} checked={formData.overDueSkilsReminder} onChange={e => setFormData(prev => ({...prev, overDueSkilsReminder: e.target.checked}))}/>
												<Typography>Overdue Training</Typography>
											</Stack>
										</Box>
										<Box
											onClick={() => {window.location.href = (process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? "");}}
											sx={{display: "flex", gap: "10px", cursor: "pointer"}}
											role="button"
											tabIndex={0}
											data-test-id="settings-manage-account-link"
											aria-label={t("page_settings_manage_account", "Manage account details")}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													window.location.href = (process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? "");
												}
											}}
										>
											<PersonIcon color="primary" />
											<Typography>{t("page_settings_manage_account", "Manage account details")}</Typography>
										</Box>
										<Box className={classes.btnContainer} role="group" aria-label="Language preferences actions">
											<Button
												variant="contained"
												disabled={!changeMade}
												onClick={() => handleUpdate()}
												data-test-id="settings-save-button"
												aria-label={t("page_settings_save_button", "Save")}
												sx={{
													backgroundColor: palette.orange,
													color: palette.black,
													"&:hover": {
														backgroundColor: palette.orange,
														opacity: 0.9
													},
													"&:disabled": {
														backgroundColor: palette.orange,
														color: palette.black,
														opacity: 0.6
													}
												}}
											>
												{updateUserResponse.isLoading ? <CircularProgress size={20} /> : t("page_settings_save_button", "Save")}
											</Button>
											{me.data && <Button
												variant="outlined"
												disabled={!changeMade}
												onClick={() => me.data && setFormData(me.data)}
												data-test-id="settings-discard-button"
												aria-label={t("page_settings_cancel_button", "Discard")}
												sx={{
													backgroundColor: palette.black,
													color: palette.orange,
													borderColor: palette.orange,
													"&:hover": {
														backgroundColor: palette.black,
														borderColor: palette.orange,
														opacity: 0.9
													},
													"&:disabled": {
														backgroundColor: palette.black,
														color: palette.orange,
														borderColor: palette.orange,
														opacity: 0.6
													}
												}}
											>{t("page_settings_cancel_button", "Discard")}</Button>}
										</Box>
									</Box>
								</Box>
							}
						</Box>
					</AdminContainer>
				</PageBase>
			</ThemeProvider>
		</Box>
	);
}